import { useState, useEffect } from "react";
import { useMutation } from "@apollo/client";

import { useUser } from "MVVM/Hooks/useUser";
import { usePortal } from "MVVM/Hooks/usePortal";
import { UnifiedUpgradeModal } from "MVVM/Components/Modals/UnifiedUpgradeModal";
import { USER_UPDATE_AUTO_SAVE } from "MVVM/GraphQL/user";
import { analytics } from "MVVM/Analytics";

const AUTO_SAVE_ELIGIBLE_TIERS = [
  "PRO",
  "PRO_PLUS",
  "CREATOR_PLAT",
  "INTERNAL",
];

export const useAutoSave = () => {
  const { allstarUser, setAllstarUser } = useUser();
  const userType = allstarUser?.user?.userGroup?.type;
  const isEligible = Boolean(
    userType && AUTO_SAVE_ELIGIBLE_TIERS.includes(userType),
  );
  const serverValue = allstarUser?.user?.autoSaveClips ?? false;
  const [optimisticValue, setOptimisticValue] = useState(serverValue);

  const {
    portalOpen: upsellOpen,
    setPortalOpen: setUpsellOpen,
    Portal: UpsellPortal,
  } = usePortal(
    <UnifiedUpgradeModal
      featureName="Auto-save your Clips"
      upgradeAction="Auto-save your Clips"
      requiredTierKey="PRO"
      analyticsSource="Clip Settings Modal"
      onClose={() => setUpsellOpen(false)}
    />,
  );

  useEffect(() => {
    setOptimisticValue(serverValue);
  }, [serverValue]);

  const [updateUser, { loading }] = useMutation(USER_UPDATE_AUTO_SAVE, {
    onCompleted: (data) => {
      const errors = data?.userUpdate?.errors;
      if (errors?.length) {
        setOptimisticValue(serverValue);
        analytics.track("Auto-Save Toggle - Error", {
          source: "clip-settings",
          error: errors[0].message,
        });
        return;
      }

      const newValue = data?.userUpdate?.viewer?.autoSaveClips ?? !serverValue;
      setAllstarUser((prev: any) => ({
        ...prev,
        user: { ...prev.user, autoSaveClips: newValue },
      }));
      analytics.track("Auto-Save Toggle - Success", {
        source: "clip-settings",
        enabled: newValue,
      });
    },
    onError: (error) => {
      setOptimisticValue(serverValue);
      analytics.track("Auto-Save Toggle - Error", {
        source: "clip-settings",
        error: error.message,
      });
    },
  });

  const handleToggle = () => {
    if (loading) return;

    if (!isEligible) {
      analytics.track("Auto-Save Toggle - Upsell Clicked", {
        source: "clip-settings",
        userTier: userType,
      });
      setUpsellOpen(true);
      return;
    }

    const newValue = !optimisticValue;
    setOptimisticValue(newValue);
    analytics.track("Auto-Save Toggle - Toggled", {
      source: "clip-settings",
      enabled: newValue,
      previousValue: optimisticValue,
    });

    updateUser({
      variables: { input: { autoSaveClips: newValue } },
    });
  };

  return {
    optimisticValue,
    loading,
    isEligible,
    handleToggle,
    upsellOpen,
    UpsellPortal,
  };
};
